import { useCallback, useEffect, useRef, useState } from 'react';
import type { Asset, CommitResult, LivePreview, Session, World, WorldEvent } from '../shared/types';
import { api, ApiError, bootstrapSession, commitResponseIsCurrent, resetSessionBootstrap, sessionResponseIsCurrent } from './api';
import { uniqueById as unique, mergeWorld, mergeWorldCommit,mergeWorldModeration } from './worldState';
import { IdentityResponses, loadIdentityCatalog, refreshStreetIdentity } from './identityState';
import {refreshModeratedWorld} from './moderationSync';
import { boardApiUrl } from './boardRoute';
import {WorldMetadataProgress,metadataReadIsCurrent,mergeWorldMetadata,type MetadataContext,type MetadataReadStamp} from './worldMetadata';
export function useStreet() {
  const [unavailable,setUnavailable]=useState(false);
  const [accountUnavailable,setAccountUnavailable]=useState(false);
  const [authorsVersion,setAuthorsVersion]=useState(0);
  const blocked=(failure:unknown)=>failure instanceof ApiError&&(failure.status===410||failure.accountUnavailable===true);
  const showBlocked=(failure:unknown)=>{metadataProof.current=null;metadataProgress.current.clear();setUnavailable(true);setAccountUnavailable(failure instanceof ApiError&&failure.accountUnavailable===true);};
  const [resetVersion,setResetVersion]=useState(0);
  const epoch=useRef<number|null>(null);
  const synchronizedModeration=useRef(0);
  const reset=(next:number)=>{if(epoch.current!==null&&next>epoch.current){metadataProof.current=null;metadataProgress.current.clear();synchronizedModeration.current=0;requested.current=[0];latestOpening.current=0;setOpening(null);previewRows.current.clear();pendingModerations.current.clear();pendingCommits.current.clear();pendingAssets.current.clear();setPreviews([]);setResetVersion(v=>v+1);}if(epoch.current===null||next>epoch.current)epoch.current=next;};
  const [identityVersion, setIdentityVersion] = useState(0);
  const identity=useRef(new IdentityResponses());
  const [world, setWorld] = useState<World | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [status, setStatus] = useState<'connecting' | 'live' | 'offline'>('connecting');
  const [connectionError,setConnectionError]=useState('');
  const connectionRetry=useRef<()=>void>(()=>{});
  const retryConnection=useCallback(()=>connectionRetry.current(),[]);
  const [error, setError] = useState('');
  const [opening, setOpening] = useState<number | null>(null);
  const [catalog, setCatalog] = useState<Asset[]>([]);
  const [previews,setPreviews]=useState<LivePreview[]>([]);
  const previewRows=useRef(new Map<string,LivePreview>());
  const endedPreviews=useRef(new Map<string,number>());
  const requested = useRef([0]);
  const started = useRef(false);
  const mounted = useRef(true);
  const reloadVersion = useRef(0);
  const worldRef = useRef<World | null>(null);
  const sessionRef=useRef<Session|null>(null);
  const latestOpening = useRef<number|null>(null);
  const initialPlayer = useRef<{stamp:number;player:Session}|null>(null);
  const pendingModerations=useRef(new Map<string,import('../shared/moderation').LayerActionResult>());
  const pendingCommits=useRef(new Map<string,Extract<WorldEvent,{type:'placement'}>>());
  const pendingAssets=useRef(new Map<string,Asset>());
  const metadataProgress=useRef(new WorldMetadataProgress());
  const metadataProof=useRef<MetadataContext|null>(null);
  const metadataPending=useRef(false);
  const foregroundPending=useRef(false);
  const metadataFailed=useRef(false);
  const mutationVersion=useRef(0);
  const [metadataVersion,setMetadataVersion]=useState(0);
  worldRef.current = world;
  sessionRef.current=session;
  const announce = useCallback((id:number) => {if(latestOpening.current===null){latestOpening.current=id;return;}if(id>latestOpening.current){latestOpening.current=id;setOpening(id);}},[]);

  const reload = useCallback(async (blocks?:number[], fresh = false) => {
    // A retry/recovery without a viewport selection deliberately refreshes the
    // authoritative world. Only ordinary viewport requests can reuse metadata.
    if(blocks===undefined){blocks=requested.current;fresh=fresh||worldRef.current!==null;}
    blocks=[...new Set(blocks)].filter(id=>Number.isSafeInteger(id)&&id>=0).slice(0,6);
    if(!blocks.length)blocks=[0];
    requested.current = blocks;
    const proof=metadataProof.current,current=worldRef.current;
    if(!fresh&&!foregroundPending.current&&!metadataFailed.current&&proof&&current&&identity.current.current(proof.identity)&&sessionRef.current?.id===proof.writerId&&(current.worldEpoch??0)===proof.worldEpoch&&(current.moderationRevision??0)===proof.moderationRevision&&metadataProgress.current.has(proof,blocks)){
      setMetadataVersion(value=>value+1);return;
    }
    const version = ++reloadVersion.current;
    foregroundPending.current=true;
    const identityStamp=identity.current.stamp();
    try {
      await bootstrapSession();
      const first=initialPlayer.current;
      initialPlayer.current=null;
      const [snapshot, player] = await Promise.all([api.world(blocks,fresh,epoch.current??0), first?.stamp===identityStamp&&!fresh?Promise.resolve(first.player):api.session(fresh)]);
      if (!mounted.current || version !== reloadVersion.current || !identity.current.current(identityStamp)) return;
      if((snapshot.worldEpoch??0)<(epoch.current??0))return;
      const next=await refreshModeratedWorld(snapshot,worldRef.current,blocks,synchronizedModeration.current,selected=>api.world(selected,true,epoch.current??0));
      if(!mounted.current||version!==reloadVersion.current||!identity.current.current(identityStamp))return;
      synchronizedModeration.current=next.moderationRevision??0;
      reset(next.worldEpoch??0);
      const nextProof:MetadataContext={identity:identityStamp,writerId:player.id,worldEpoch:next.worldEpoch??0,moderationRevision:next.moderationRevision??0};
      metadataProof.current=nextProof;
      metadataProgress.current.record(nextProof,[...new Set([...blocks,...next.surfaces.map(surface=>surface.blockId)])].filter(id=>id<=next.frontier&&next.blocks.some(block=>block.id===id)));
      metadataFailed.current=false;
      const commits=[...pendingCommits.current.values()], assets=[...pendingAssets.current.values()];
      const moderations=[...pendingModerations.current.values()];pendingModerations.current.clear();
      pendingCommits.current.clear();pendingAssets.current.clear();
      setWorld(old => {
        let merged=old?mergeWorld(old,next):next;
        for(const event of commits)if((event.worldEpoch??0)===(merged.worldEpoch??0))merged=mergeWorldCommit(merged,event);
        for(const result of moderations)merged=mergeWorldModeration(merged,result);
        return {...merged,assets:unique([...merged.assets,...assets])};
      });
      announce(next.frontier);
      if(sessionResponseIsCurrent(player))identity.current.accept(identityStamp,player,setSession); setError('');setUnavailable(false);setAccountUnavailable(false);
      if (!started.current) {
        started.current = true;
        const frontierBlocks = [Math.max(0, next.frontier - 1), next.frontier, next.frontier + 1].filter(id => id <= next.frontier);
        if (!frontierBlocks.every(id => blocks.includes(id))) void reload(frontierBlocks);
      }
    } catch (failure) { if (mounted.current && version === reloadVersion.current && identity.current.current(identityStamp)) {metadataFailed.current=true;if(blocked(failure))showBlocked(failure);setError(failure instanceof Error ? failure.message : 'The street is unavailable.');} }
    finally{if(mounted.current&&version===reloadVersion.current){foregroundPending.current=false;setMetadataVersion(value=>value+1);}}
  }, [announce]);

  useEffect(()=>{
    // Foreground session/world proof owns publication. These batches add only
    // missing selected metadata and never advance the foreground request token.
    const proof=metadataProof.current;
    const available=()=>mounted.current&&!unavailable&&!foregroundPending.current&&navigator.onLine!==false&&document.visibilityState!=='hidden';
    if(!world||!session||!proof||!available()||metadataPending.current||metadataFailed.current||!identity.current.current(proof.identity)||session.id!==proof.writerId||(world.worldEpoch??0)!==proof.worldEpoch||(world.moderationRevision??0)!==proof.moderationRevision)return;
    const batch=metadataProgress.current.next(proof,world,requested.current);
    if(!batch.length)return;
    const stamp:MetadataReadStamp={...proof,foregroundVersion:reloadVersion.current,mutationVersion:mutationVersion.current};
    const current=():MetadataReadStamp|null=>{
      const latest=metadataProof.current;
      return latest&&available()&&identity.current.current(latest.identity)?{...latest,foregroundVersion:reloadVersion.current,mutationVersion:mutationVersion.current}:null;
    };
    metadataPending.current=true;
    void api.world(batch,true,proof.worldEpoch).then(snapshot=>{
      if(!metadataReadIsCurrent(stamp,current()))return;
      if((snapshot.worldEpoch??0)!==proof.worldEpoch||(snapshot.moderationRevision??0)!==proof.moderationRevision){void reload(requested.current,true);return;}
      setWorld(old=>{
        if(!old||!metadataReadIsCurrent(stamp,current()))return old;
        const merged=mergeWorldMetadata(old,snapshot);
        if(merged)metadataProgress.current.record(proof,batch);
        return merged??old;
      });
    }).catch(failure=>{
      if(!metadataReadIsCurrent(stamp,current()))return;
      metadataFailed.current=true;
      if(blocked(failure))showBlocked(failure);
    }).finally(()=>{metadataPending.current=false;if(mounted.current)setMetadataVersion(value=>value+1);});
  },[world,session,metadataVersion,identityVersion,unavailable,status,reload]);

  useEffect(() => {
    mounted.current = true;
    let cancelled=false;
    const identityStamp=identity.current.stamp();
    let events:EventSource|null=null;
    let retryTimer:ReturnType<typeof setTimeout>|null=null;
    let fallbackTimer:ReturnType<typeof setTimeout>|null=null;
    let established=false;
    let hasReady=false;
    let checkingAccess:EventSource|null=null;
    let readyVersion=0;
    const setup=async()=>{
    if(cancelled)return;
    try {
      const player=await bootstrapSession();
      if(cancelled||!identity.current.current(identityStamp))return;
      identity.current.accept(identityStamp,player,setSession);
      initialPlayer.current={stamp:identityStamp,player};
    }catch(failure){if(!cancelled&&identity.current.current(identityStamp)){setError(failure instanceof Error?failure.message:'The street is unavailable.');setStatus('offline');if(blocked(failure))showBlocked(failure);else retryTimer=setTimeout(()=>void setup(),2000);}return;}
    void loadIdentityCatalog(identity.current,identityStamp,api.assets,assets=>{if(!cancelled)setCatalog(assets);});
    established=true;
    connect();
    // Normally the ready handshake starts the one initial snapshot. If realtime
    // is unavailable, the street still loads rather than waiting indefinitely.
    fallbackTimer=setTimeout(()=>{fallbackTimer=null;void reload();},400);
    };
    const connect=()=>{
    if(cancelled||events||!established||!identity.current.current(identityStamp)||navigator.onLine===false||document.visibilityState==='hidden')return;
    setStatus('connecting');
    events = new EventSource(boardApiUrl('/api/events'));
    events.onerror = () => {
      if(cancelled||!identity.current.current(identityStamp))return;
      previewRows.current.clear();setPreviews([]);setStatus(navigator.onLine ? 'connecting' : 'offline');
      const failedStream=events,readyStamp=readyVersion;
      setConnectionError(navigator.onLine?(failedStream?.readyState===EventSource.CLOSED?'Live updates could not connect. Your draft is still here.':'Live updates are unavailable. Retrying automatically. Your draft is still here.'):'');
      if(!failedStream||checkingAccess===failedStream)return;
      checkingAccess=failedStream;
      void api.session().catch(failure=>{if(!cancelled&&events===failedStream&&readyVersion===readyStamp&&identity.current.current(identityStamp)&&blocked(failure)){reloadVersion.current++;showBlocked(failure);setStatus('offline');disconnect();established=false;}}).finally(()=>{if(checkingAccess===failedStream)checkingAccess=null;});
    };
    events.onmessage = message => {
      if(cancelled||!identity.current.current(identityStamp))return;
      let event: WorldEvent;
      try { event = JSON.parse(message.data) as WorldEvent; } catch { return; }
      if(event.type==='board-unavailable'||event.type==='account-unavailable'){reloadVersion.current++;identity.current.begin();resetSessionBootstrap();setUnavailable(true);setAccountUnavailable(event.type==='account-unavailable');setStatus('offline');disconnect();established=false;return;}
      if(event.type==='layer-moderated'){if(event.worldEpoch!==(epoch.current??0))return;mutationVersion.current++;pendingModerations.current.set(event.placement.id,event);setWorld(old=>old?mergeWorldModeration(old,event):old);void reload(requested.current,true);return;}
      if(event.type==='authors-updated'){reloadVersion.current++;setAuthorsVersion(v=>v+1);void reload(requested.current,true);return;}
      if(event.type==='balances-reset'){identity.current.begin();resetSessionBootstrap();setIdentityVersion(v=>v+1);return;}
      if (event.type === 'presence') setWorld(old => old ? { ...old, online: event.online } : old);
      if(event.type==='world-reset'){reset(event.worldEpoch);void reload([0],true);return;}
      if(event.type==='ready')reset(event.worldEpoch??0);
      if (event.type === 'ready') {
        readyVersion++;
        setStatus('live');setConnectionError('');
        if(fallbackTimer){clearTimeout(fallbackTimer);fallbackTimer=null;}
        previewRows.current.clear();setPreviews([]);
        // A reconnect (or snapshot started by the fallback) needs a read after
        // this handshake; merely joining an older request could miss its gap.
        const fresh=hasReady||worldRef.current!==null||initialPlayer.current===null;
        hasReady=true;void reload(requested.current,fresh);
      }
      if(event.type==='preview'){
        if((event.preview.worldEpoch??0)!==(epoch.current??0))return;
        const p=event.preview,old=previewRows.current.get(p.writerId);
        if(Date.parse(p.expiresAt)>Date.now()&&!endedPreviews.current.has(p.id)&&(!old||old.id!==p.id||p.revision>old.revision)){
          previewRows.current.set(p.writerId,p);setPreviews([...previewRows.current.values()]);
        }
      }
      if(event.type==='preview-ended'){
        endedPreviews.current.set(event.id,Date.now()+60_000);
        if(previewRows.current.get(event.writerId)?.id===event.id){previewRows.current.delete(event.writerId);setPreviews([...previewRows.current.values()]);}
      }
      if (event.type === 'placement') {
        if((event.worldEpoch??0)!==(epoch.current??0))return;
        mutationVersion.current++;
        if(!worldRef.current||(worldRef.current.worldEpoch??0)!==(epoch.current??0))pendingCommits.current.set(event.placement.id,event);
        setWorld(old => old ? mergeWorldCommit(old,event) : old);
        if (event.unlockedBlock) { announce(event.unlockedBlock.id); void reload([...new Set([...requested.current, event.unlockedBlock.id])],true); }
        else if (!worldRef.current?.assets.some(a => a.id === event.placement.assetId)) {
          const generation=epoch.current??0;
          void api.asset(event.placement.assetId,generation).then(asset=>{
            if(cancelled||!mounted.current||!identity.current.current(identityStamp)||epoch.current!==generation)return;
            if(!worldRef.current||(worldRef.current.worldEpoch??0)!==generation)pendingAssets.current.set(asset.id,asset);
            setWorld(old=>old&&(old.worldEpoch??0)===generation?{...old,assets:unique([...old.assets,asset])}:old);
          }).catch(failure=>{if(!cancelled&&identity.current.current(identityStamp)&&epoch.current===generation){if(blocked(failure))showBlocked(failure);else void reload(requested.current,true);}});
        }
        void api.session(true).then(player => { if (!cancelled&&mounted.current&&sessionResponseIsCurrent(player)) identity.current.accept(identityStamp,player,setSession); }).catch(failure=>{if(!cancelled&&identity.current.current(identityStamp)&&blocked(failure)){showBlocked(failure);disconnect();}});
      }
    };
    };
    const disconnect=()=>{if(events){events.onopen=null;events.onmessage=null;events.onerror=null;events.close();events=null;}previewRows.current.clear();setPreviews([]);};
    connectionRetry.current=()=>{if(cancelled)return;disconnect();connect();};
    void setup();
    const offline = () => {disconnect();setStatus('offline');};
    const online = () => {setStatus('connecting');const connected=!!events;connect();if(connected)void reload(requested.current,true);};
    const wake = () => {
      if(document.visibilityState==='visible'){const connected=!!events;connect();if(connected)void reload(requested.current,true);}
      else {disconnect();setStatus(navigator.onLine?'connecting':'offline');}
    };
    window.addEventListener('offline', offline); window.addEventListener('online', online); document.addEventListener('visibilitychange', wake);
    return () => { cancelled=true;mounted.current = false;connectionRetry.current=()=>{};if(retryTimer)clearTimeout(retryTimer);if(fallbackTimer)clearTimeout(fallbackTimer);events?.close(); window.removeEventListener('offline', offline); window.removeEventListener('online', online); document.removeEventListener('visibilitychange', wake); };
  }, [reload,announce,identityVersion]);
  useEffect(()=>{
    const timer=setInterval(()=>{
      const now=Date.now();let changed=false;
      for(const [writer,p] of previewRows.current)if(Date.parse(p.expiresAt)<=now){previewRows.current.delete(writer);changed=true;}
      for(const [id,expiry] of endedPreviews.current)if(expiry<=now)endedPreviews.current.delete(id);
      if(changed)setPreviews([...previewRows.current.values()]);
    },1000);
    return()=>clearInterval(timer);
  },[]);

  const addAsset = useCallback((asset: Asset) => {
    setCatalog(old => unique([...old, asset]));
    setWorld(old => old ? { ...old, assets: unique([...old.assets, asset]) } : old);
  }, []);
  const applyCommit = useCallback((result: CommitResult) => {
    if((result.worldEpoch??0)!==(epoch.current??0))return false;
    mutationVersion.current++;
    if(commitResponseIsCurrent(result)&&identity.current.matches(result.session))identity.current.accept(identity.current.stamp(),result.session,setSession);
    setWorld(old => old ? mergeWorldCommit(old,result) : old);
    if (result.unlockedBlock) { announce(result.unlockedBlock.id); void reload([...new Set([...requested.current, result.unlockedBlock.id])]); }
    return true;
  }, [reload,announce]);
  useEffect(()=>{
    if(!session?.nextResetAt)return;
    let cancelled=false;
    const identityStamp=identity.current.stamp();
    let timer:ReturnType<typeof setTimeout>;
    const renew=async()=>{try{const player=await api.session(true);if(!cancelled&&sessionResponseIsCurrent(player))identity.current.accept(identityStamp,player,setSession);}catch{if(!cancelled&&identity.current.current(identityStamp))timer=setTimeout(()=>void renew(),10000);}};
    timer=setTimeout(()=>void renew(),Math.max(1000,new Date(session.nextResetAt!).getTime()-Date.now()+250));
    return()=>{cancelled=true;clearTimeout(timer);};
  },[session?.nextResetAt,session?.id]);
  const applyModeration=useCallback((result:import('../shared/moderation').LayerActionResult)=>{if(result.worldEpoch!==(epoch.current??0))return;mutationVersion.current++;pendingModerations.current.set(result.placement.id,result);setWorld(old=>old?mergeWorldModeration(old,result):old);void reload(requested.current,true);},[reload]);
  const refreshIdentity = useCallback(async () => {
    await refreshStreetIdentity(identity.current,{resetBootstrap:resetSessionBootstrap,clearCatalog:()=>{metadataProof.current=null;metadataProgress.current.clear();setCatalog([]);previewRows.current.clear();setPreviews([]);},reconnect:()=>setIdentityVersion(version=>version+1),session:bootstrapSession,publish:player=>{if(mounted.current)setSession(player);}});
  }, []);
  const proof=metadataProof.current;
  const artworkReady=!!world&&!!session&&!unavailable&&!!proof&&identity.current.current(proof.identity)&&session.id===proof.writerId&&(world.worldEpoch??0)===proof.worldEpoch&&(world.moderationRevision??0)===proof.moderationRevision;
  return { artworkReady,unavailable,accountUnavailable,authorsVersion, resetVersion, refreshIdentity, world, session, status, connectionError,retryConnection,error, opening, setOpening, catalog, previews, reload, addAsset, applyCommit,applyModeration };
}
